import { DataGrid, DataGridFilterState, Spinner } from '@ibsys/kit';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BitrixTouchSendStatus } from '@/entities/integrations';
import { useBitrixTouchSends } from '@/entities/integrations';
import { dataGridHeightByRows } from '@/shared/lib';
import { fromDataGridFilterValue } from '@/shared/lib/utils/tableFilterValue';

import { useBitrixTouchSendColumns } from './useBitrixTouchSendColumns';

const PAGE_SIZE = 20;

/** Потолок перечня в карточке: страница целиком выше — остаток прокручивается внутри самого грида */
const JOURNAL_MAX_HEIGHT = 480;

/** Отбор по исходу: значение отбора приходит от грида, а в запрос идут только известные исходы */
const toStatusFilter = (filters: DataGridFilterState): BitrixTouchSendStatus | undefined => {
  const value = fromDataGridFilterValue(filters.status);
  return value === 'sent' || value === 'error' ? value : undefined;
};

/**
 * Перечень заведённых касаний Bitrix24: по строке на диалог, от новых к старым, с исходом
 * попытки и причиной неудачи. Листается и отбирается по исходу на сервере.
 */
export const BitrixTouchSendsJournal: FC = () => {
  const { t } = useTranslation(['integrations']);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<DataGridFilterState>({});
  const columns = useBitrixTouchSendColumns();

  const status = toStatusFilter(filters);
  const { data, isLoading } = useBitrixTouchSends({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    status,
  });

  const items = data?.items ?? [];

  // Отбор применяется ко всему перечню, а не к открытой странице: прежнее смещение на новом
  // отборе показало бы пустую страницу при ненулевом счётчике
  const handleFiltersChange = (nextFilters: DataGridFilterState) => {
    setFilters(nextFilters);
    setPage(0);
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="font-medium">{t('integrations:bitrixTouch.journal.title')}</p>
      <DataGrid
        className="w-full"
        columns={columns}
        dataSource={{
          mode: 'paginated',
          totalCount: data?.total ?? 0,
          page,
          pageSize: PAGE_SIZE,
          onPageChange: setPage,
          data: items,
        }}
        getRowId={(row) => String(row.conversationId)}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        height={dataGridHeightByRows(items.length, JOURNAL_MAX_HEIGHT)}
        emptyState={
          isLoading ? (
            <Spinner className="size-6" />
          ) : (
            <span className="text-muted-foreground">
              {/* Пустая выдача под отбором — не пустой перечень: иначе заведённые касания выглядят отсутствующими */}
              {t(status ? 'integrations:bitrixTouch.journal.emptyByFilter' : 'integrations:bitrixTouch.journal.empty')}
            </span>
          )
        }
        ariaLabel={t('integrations:bitrixTouch.journal.title')}
      />
    </div>
  );
};
